// Public holiday calendars per country (CEN-801).
// Rule types:
//   fixed          { month, day }
//   date           { year, month, day }  exact year-specific date
//   easter         { offset }            days from Easter Sunday (Gregorian computus)
//   nthWeekday     { month, weekday, nth }  weekday: 0=Sunday..6=Saturday
//   lastWeekday    { month, weekday }
//   weekdayBetween { month, weekday, from, to }  e.g. Midsummer: Saturday 20-26 June
// Optional: untilYear (rule not valid after that year), fromYear (not valid before).

const pad = (n) => (n < 10 ? '0' + n : '' + n);

// Gregorian computus (Anonymous Gregorian algorithm)
const easterSunday = (year) => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
};

const toISO = (d) => d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());

const resolveRule = (rule, year) => {
    if (typeof rule.fromYear !== 'undefined' && year < rule.fromYear) return null;
    if (typeof rule.untilYear !== 'undefined' && year > rule.untilYear) return null;
    switch (rule.type) {
        case 'fixed':
            return new Date(year, rule.month - 1, rule.day);
        case 'date':
            return year === rule.year ? new Date(year, rule.month - 1, rule.day) : null;
        case 'easter': {
            const d = easterSunday(year);
            d.setDate(d.getDate() + rule.offset);
            return d;
        }
        case 'nthWeekday': {
            const first = new Date(year, rule.month - 1, 1);
            const delta = (rule.weekday - first.getDay() + 7) % 7;
            const day = 1 + delta + (rule.nth - 1) * 7;
            return new Date(year, rule.month - 1, day);
        }
        case 'lastWeekday': {
            const last = new Date(year, rule.month, 0);
            const delta = (last.getDay() - rule.weekday + 7) % 7;
            return new Date(year, rule.month - 1, last.getDate() - delta);
        }
        case 'weekdayBetween': {
            for (let day = rule.from; day <= rule.to; day++) {
                const d = new Date(year, rule.month - 1, day);
                if (d.getDay() === rule.weekday) return d;
            }
            return null;
        }
        default:
            return null;
    }
};

const HOLIDAY_COUNTRIES = [
    { code: 'AT', name: 'Austria', rules: [
        { type: 'fixed', month: 1, day: 1, name: "New Year's Day" },
        { type: 'fixed', month: 1, day: 6, name: 'Epiphany' },
        { type: 'easter', offset: 1, name: 'Easter Monday' },
        { type: 'fixed', month: 5, day: 1, name: 'Labour Day' },
        { type: 'easter', offset: 39, name: 'Ascension Day' },
        { type: 'easter', offset: 50, name: 'Whit Monday' },
        { type: 'easter', offset: 60, name: 'Corpus Christi' },
        { type: 'fixed', month: 8, day: 15, name: 'Assumption Day' },
        { type: 'fixed', month: 10, day: 26, name: 'National Day' },
        { type: 'fixed', month: 11, day: 1, name: "All Saints' Day" },
        { type: 'fixed', month: 12, day: 8, name: 'Immaculate Conception' },
        { type: 'fixed', month: 12, day: 25, name: 'Christmas Day' },
        { type: 'fixed', month: 12, day: 26, name: "St. Stephen's Day" },
    ]},
    { code: 'AU', name: 'Australia', rules: [
        { type: 'fixed', month: 1, day: 1, name: "New Year's Day" },
        { type: 'fixed', month: 1, day: 26, name: 'Australia Day' },
        { type: 'easter', offset: -2, name: 'Good Friday' },
        { type: 'easter', offset: 1, name: 'Easter Monday' },
        { type: 'fixed', month: 4, day: 25, name: 'Anzac Day' },
        { type: 'nthWeekday', month: 6, weekday: 1, nth: 2, name: "King's Birthday" },
        { type: 'fixed', month: 12, day: 25, name: 'Christmas Day' },
        { type: 'fixed', month: 12, day: 26, name: 'Boxing Day' },
    ]},
    { code: 'BE', name: 'Belgium', rules: [
        { type: 'fixed', month: 1, day: 1, name: "New Year's Day" },
        { type: 'easter', offset: 1, name: 'Easter Monday' },
        { type: 'fixed', month: 5, day: 1, name: 'Labour Day' },
        { type: 'easter', offset: 39, name: 'Ascension Day' },
        { type: 'easter', offset: 50, name: 'Whit Monday' },
        { type: 'fixed', month: 7, day: 21, name: 'National Day' },
        { type: 'fixed', month: 8, day: 15, name: 'Assumption Day' },
        { type: 'fixed', month: 11, day: 1, name: "All Saints' Day" },
        { type: 'fixed', month: 11, day: 11, name: 'Armistice Day' },
        { type: 'fixed', month: 12, day: 25, name: 'Christmas Day' },
    ]},
    { code: 'CA', name: 'Canada', rules: [
        { type: 'fixed', month: 1, day: 1, name: "New Year's Day" },
        { type: 'easter', offset: -2, name: 'Good Friday' },
        { type: 'fixed', month: 7, day: 1, name: 'Canada Day' },
        { type: 'nthWeekday', month: 9, weekday: 1, nth: 1, name: 'Labour Day' },
        { type: 'fixed', month: 9, day: 30, name: 'National Day for Truth and Reconciliation' },
        { type: 'nthWeekday', month: 10, weekday: 1, nth: 2, name: 'Thanksgiving' },
        { type: 'fixed', month: 11, day: 11, name: 'Remembrance Day' },
        { type: 'fixed', month: 12, day: 25, name: 'Christmas Day' },
        { type: 'fixed', month: 12, day: 26, name: 'Boxing Day' },
    ]},
    { code: 'CH', name: 'Switzerland', rules: [
        { type: 'fixed', month: 1, day: 1, name: "New Year's Day" },
        { type: 'fixed', month: 1, day: 2, name: "Berchtold's Day" },
        { type: 'easter', offset: -2, name: 'Good Friday' },
        { type: 'easter', offset: 1, name: 'Easter Monday' },
        { type: 'easter', offset: 39, name: 'Ascension Day' },
        { type: 'easter', offset: 50, name: 'Whit Monday' },
        { type: 'fixed', month: 8, day: 1, name: 'National Day' },
        { type: 'fixed', month: 12, day: 25, name: 'Christmas Day' },
        { type: 'fixed', month: 12, day: 26, name: "St. Stephen's Day" },
    ]},
    { code: 'DE', name: 'Germany', rules: [
        { type: 'fixed', month: 1, day: 1, name: "New Year's Day" },
        { type: 'easter', offset: -2, name: 'Good Friday' },
        { type: 'easter', offset: 1, name: 'Easter Monday' },
        { type: 'fixed', month: 5, day: 1, name: 'Labour Day' },
        { type: 'easter', offset: 39, name: 'Ascension Day' },
        { type: 'easter', offset: 50, name: 'Whit Monday' },
        { type: 'fixed', month: 10, day: 3, name: 'German Unity Day' },
        { type: 'fixed', month: 12, day: 25, name: 'Christmas Day' },
        { type: 'fixed', month: 12, day: 26, name: 'Second Day of Christmas' },
    ]},
    { code: 'DK', name: 'Denmark', rules: [
        { type: 'fixed', month: 1, day: 1, name: "New Year's Day" },
        { type: 'easter', offset: -3, name: 'Maundy Thursday' },
        { type: 'easter', offset: -2, name: 'Good Friday' },
        { type: 'easter', offset: 1, name: 'Easter Monday' },
        { type: 'easter', offset: 26, name: 'Great Prayer Day', untilYear: 2023 },
        { type: 'easter', offset: 39, name: 'Ascension Day' },
        { type: 'easter', offset: 50, name: 'Whit Monday' },
        { type: 'fixed', month: 6, day: 5, name: 'Constitution Day' },
        { type: 'fixed', month: 12, day: 25, name: 'Christmas Day' },
        { type: 'fixed', month: 12, day: 26, name: 'Second Day of Christmas' },
    ]},
    { code: 'ES', name: 'Spain', rules: [
        { type: 'fixed', month: 1, day: 1, name: "New Year's Day" },
        { type: 'fixed', month: 1, day: 6, name: 'Epiphany' },
        { type: 'easter', offset: -3, name: 'Maundy Thursday' },
        { type: 'easter', offset: -2, name: 'Good Friday' },
        { type: 'fixed', month: 5, day: 1, name: 'Labour Day' },
        { type: 'fixed', month: 8, day: 15, name: 'Assumption Day' },
        { type: 'fixed', month: 10, day: 12, name: 'National Day' },
        { type: 'fixed', month: 11, day: 1, name: "All Saints' Day" },
        { type: 'fixed', month: 12, day: 6, name: 'Constitution Day' },
        { type: 'fixed', month: 12, day: 8, name: 'Immaculate Conception' },
        { type: 'fixed', month: 12, day: 25, name: 'Christmas Day' },
    ]},
    { code: 'FI', name: 'Finland', rules: [
        { type: 'fixed', month: 1, day: 1, name: "New Year's Day" },
        { type: 'fixed', month: 1, day: 6, name: 'Epiphany' },
        { type: 'easter', offset: -2, name: 'Good Friday' },
        { type: 'easter', offset: 1, name: 'Easter Monday' },
        { type: 'fixed', month: 5, day: 1, name: 'May Day' },
        { type: 'easter', offset: 39, name: 'Ascension Day' },
        { type: 'weekdayBetween', month: 6, weekday: 6, from: 20, to: 26, name: 'Midsummer Day' },
        { type: 'fixed', month: 12, day: 6, name: 'Independence Day' },
        { type: 'fixed', month: 12, day: 25, name: 'Christmas Day' },
        { type: 'fixed', month: 12, day: 26, name: "St. Stephen's Day" },
    ]},
    { code: 'FR', name: 'France', rules: [
        { type: 'fixed', month: 1, day: 1, name: "New Year's Day" },
        { type: 'easter', offset: 1, name: 'Easter Monday' },
        { type: 'fixed', month: 5, day: 1, name: 'Labour Day' },
        { type: 'fixed', month: 5, day: 8, name: 'Victory Day' },
        { type: 'easter', offset: 39, name: 'Ascension Day' },
        { type: 'easter', offset: 50, name: 'Whit Monday' },
        { type: 'fixed', month: 7, day: 14, name: 'Bastille Day' },
        { type: 'fixed', month: 8, day: 15, name: 'Assumption Day' },
        { type: 'fixed', month: 11, day: 1, name: "All Saints' Day" },
        { type: 'fixed', month: 11, day: 11, name: 'Armistice Day' },
        { type: 'fixed', month: 12, day: 25, name: 'Christmas Day' },
    ]},
    { code: 'GB', name: 'United Kingdom', rules: [
        { type: 'fixed', month: 1, day: 1, name: "New Year's Day" },
        { type: 'easter', offset: -2, name: 'Good Friday' },
        { type: 'easter', offset: 1, name: 'Easter Monday' },
        { type: 'nthWeekday', month: 5, weekday: 1, nth: 1, name: 'Early May Bank Holiday' },
        { type: 'lastWeekday', month: 5, weekday: 1, name: 'Spring Bank Holiday' },
        { type: 'lastWeekday', month: 8, weekday: 1, name: 'Summer Bank Holiday' },
        { type: 'fixed', month: 12, day: 25, name: 'Christmas Day' },
        { type: 'fixed', month: 12, day: 26, name: 'Boxing Day' },
    ]},
    { code: 'IE', name: 'Ireland', rules: [
        { type: 'fixed', month: 1, day: 1, name: "New Year's Day" },
        { type: 'nthWeekday', month: 2, weekday: 1, nth: 1, name: "St. Brigid's Day", fromYear: 2023 },
        { type: 'fixed', month: 3, day: 17, name: "St. Patrick's Day" },
        { type: 'easter', offset: 1, name: 'Easter Monday' },
        { type: 'nthWeekday', month: 5, weekday: 1, nth: 1, name: 'May Day' },
        { type: 'nthWeekday', month: 6, weekday: 1, nth: 1, name: 'June Bank Holiday' },
        { type: 'nthWeekday', month: 8, weekday: 1, nth: 1, name: 'August Bank Holiday' },
        { type: 'lastWeekday', month: 10, weekday: 1, name: 'October Bank Holiday' },
        { type: 'fixed', month: 12, day: 25, name: 'Christmas Day' },
        { type: 'fixed', month: 12, day: 26, name: "St. Stephen's Day" },
    ]},
    { code: 'IN', name: 'India', rules: [
        { type: 'fixed', month: 1, day: 26, name: 'Republic Day' },
        { type: 'fixed', month: 8, day: 15, name: 'Independence Day' },
        { type: 'fixed', month: 10, day: 2, name: 'Gandhi Jayanti' },
        { type: 'fixed', month: 12, day: 25, name: 'Christmas Day' },
    ]},
    { code: 'IT', name: 'Italy', rules: [
        { type: 'fixed', month: 1, day: 1, name: "New Year's Day" },
        { type: 'fixed', month: 1, day: 6, name: 'Epiphany' },
        { type: 'easter', offset: 1, name: 'Easter Monday' },
        { type: 'fixed', month: 4, day: 25, name: 'Liberation Day' },
        { type: 'fixed', month: 5, day: 1, name: 'Labour Day' },
        { type: 'fixed', month: 6, day: 2, name: 'Republic Day' },
        { type: 'fixed', month: 8, day: 15, name: 'Assumption Day' },
        { type: 'fixed', month: 11, day: 1, name: "All Saints' Day" },
        { type: 'fixed', month: 12, day: 8, name: 'Immaculate Conception' },
        { type: 'fixed', month: 12, day: 25, name: 'Christmas Day' },
        { type: 'fixed', month: 12, day: 26, name: "St. Stephen's Day" },
    ]},
    { code: 'MY', name: 'Malaysia', rules: [
        { type: 'fixed', month: 1, day: 1, name: "New Year's Day" },
        { type: 'fixed', month: 2, day: 1, name: 'Federal Territory Day' },
        { type: 'date', year: 2026, month: 2, day: 17, name: 'Chinese New Year' },
        { type: 'date', year: 2026, month: 2, day: 18, name: 'Chinese New Year (Day 2)' },
        { type: 'date', year: 2026, month: 3, day: 20, name: 'Hari Raya Aidilfitri Holiday' },
        { type: 'date', year: 2026, month: 3, day: 21, name: 'Hari Raya Aidilfitri' },
        { type: 'date', year: 2026, month: 3, day: 22, name: 'Hari Raya Aidilfitri (Day 2)' },
        { type: 'fixed', month: 5, day: 1, name: 'Labour Day' },
        { type: 'date', year: 2026, month: 5, day: 27, name: 'Hari Raya Qurban' },
        { type: 'date', year: 2026, month: 5, day: 31, name: 'Wesak Day' },
        { type: 'date', year: 2026, month: 6, day: 1, name: "Yang di-Pertuan Agong's Birthday" },
        { type: 'date', year: 2026, month: 6, day: 17, name: 'Awal Muharram' },
        { type: 'date', year: 2026, month: 8, day: 25, name: "Prophet Muhammad's Birthday" },
        { type: 'fixed', month: 8, day: 31, name: 'National Day' },
        { type: 'fixed', month: 9, day: 16, name: 'Malaysia Day' },
        { type: 'date', year: 2026, month: 11, day: 8, name: 'Deepavali' },
        { type: 'fixed', month: 12, day: 25, name: 'Christmas Day' },
    ]},
    { code: 'NL', name: 'Netherlands', rules: [
        { type: 'fixed', month: 1, day: 1, name: "New Year's Day" },
        { type: 'easter', offset: -2, name: 'Good Friday' },
        { type: 'easter', offset: 1, name: 'Easter Monday' },
        { type: 'fixed', month: 4, day: 27, name: "King's Day" },
        { type: 'fixed', month: 5, day: 5, name: 'Liberation Day' },
        { type: 'easter', offset: 39, name: 'Ascension Day' },
        { type: 'easter', offset: 50, name: 'Whit Monday' },
        { type: 'fixed', month: 12, day: 25, name: 'Christmas Day' },
        { type: 'fixed', month: 12, day: 26, name: 'Second Day of Christmas' },
    ]},
    { code: 'NO', name: 'Norway', rules: [
        { type: 'fixed', month: 1, day: 1, name: "New Year's Day" },
        { type: 'easter', offset: -3, name: 'Maundy Thursday' },
        { type: 'easter', offset: -2, name: 'Good Friday' },
        { type: 'easter', offset: 1, name: 'Easter Monday' },
        { type: 'fixed', month: 5, day: 1, name: 'Labour Day' },
        { type: 'fixed', month: 5, day: 17, name: 'Constitution Day' },
        { type: 'easter', offset: 39, name: 'Ascension Day' },
        { type: 'easter', offset: 50, name: 'Whit Monday' },
        { type: 'fixed', month: 12, day: 25, name: 'Christmas Day' },
        { type: 'fixed', month: 12, day: 26, name: 'Boxing Day' },
    ]},
    { code: 'NZ', name: 'New Zealand', rules: [
        { type: 'fixed', month: 1, day: 1, name: "New Year's Day" },
        { type: 'fixed', month: 1, day: 2, name: 'Day after New Year' },
        { type: 'fixed', month: 2, day: 6, name: 'Waitangi Day' },
        { type: 'easter', offset: -2, name: 'Good Friday' },
        { type: 'easter', offset: 1, name: 'Easter Monday' },
        { type: 'fixed', month: 4, day: 25, name: 'Anzac Day' },
        { type: 'nthWeekday', month: 6, weekday: 1, nth: 1, name: "King's Birthday" },
        { type: 'nthWeekday', month: 10, weekday: 1, nth: 4, name: 'Labour Day' },
        { type: 'fixed', month: 12, day: 25, name: 'Christmas Day' },
        { type: 'fixed', month: 12, day: 26, name: 'Boxing Day' },
    ]},
    { code: 'PL', name: 'Poland', rules: [
        { type: 'fixed', month: 1, day: 1, name: "New Year's Day" },
        { type: 'fixed', month: 1, day: 6, name: 'Epiphany' },
        { type: 'easter', offset: 1, name: 'Easter Monday' },
        { type: 'fixed', month: 5, day: 1, name: 'Labour Day' },
        { type: 'fixed', month: 5, day: 3, name: 'Constitution Day' },
        { type: 'easter', offset: 60, name: 'Corpus Christi' },
        { type: 'fixed', month: 8, day: 15, name: 'Assumption Day' },
        { type: 'fixed', month: 11, day: 1, name: "All Saints' Day" },
        { type: 'fixed', month: 11, day: 11, name: 'Independence Day' },
        { type: 'fixed', month: 12, day: 25, name: 'Christmas Day' },
        { type: 'fixed', month: 12, day: 26, name: 'Second Day of Christmas' },
    ]},
    { code: 'PT', name: 'Portugal', rules: [
        { type: 'fixed', month: 1, day: 1, name: "New Year's Day" },
        { type: 'easter', offset: -2, name: 'Good Friday' },
        { type: 'fixed', month: 4, day: 25, name: 'Freedom Day' },
        { type: 'fixed', month: 5, day: 1, name: 'Labour Day' },
        { type: 'fixed', month: 6, day: 10, name: 'Portugal Day' },
        { type: 'fixed', month: 8, day: 15, name: 'Assumption Day' },
        { type: 'fixed', month: 10, day: 5, name: 'Republic Day' },
        { type: 'fixed', month: 11, day: 1, name: "All Saints' Day" },
        { type: 'fixed', month: 12, day: 1, name: 'Restoration of Independence' },
        { type: 'fixed', month: 12, day: 8, name: 'Immaculate Conception' },
        { type: 'fixed', month: 12, day: 25, name: 'Christmas Day' },
    ]},
    { code: 'SE', name: 'Sweden', rules: [
        { type: 'fixed', month: 1, day: 1, name: "New Year's Day" },
        { type: 'fixed', month: 1, day: 6, name: 'Epiphany' },
        { type: 'easter', offset: -2, name: 'Good Friday' },
        { type: 'easter', offset: 1, name: 'Easter Monday' },
        { type: 'fixed', month: 5, day: 1, name: 'May Day' },
        { type: 'easter', offset: 39, name: 'Ascension Day' },
        { type: 'fixed', month: 6, day: 6, name: 'National Day' },
        { type: 'weekdayBetween', month: 6, weekday: 6, from: 20, to: 26, name: 'Midsummer Day' },
        { type: 'fixed', month: 12, day: 25, name: 'Christmas Day' },
        { type: 'fixed', month: 12, day: 26, name: 'Boxing Day' },
    ]},
    { code: 'SG', name: 'Singapore', rules: [
        { type: 'fixed', month: 1, day: 1, name: "New Year's Day" },
        { type: 'fixed', month: 5, day: 1, name: 'Labour Day' },
        { type: 'fixed', month: 8, day: 9, name: 'National Day' },
        { type: 'fixed', month: 12, day: 25, name: 'Christmas Day' },
    ]},
    { code: 'US', name: 'United States', rules: [
        { type: 'fixed', month: 1, day: 1, name: "New Year's Day" },
        { type: 'nthWeekday', month: 1, weekday: 1, nth: 3, name: 'Martin Luther King Jr. Day' },
        { type: 'nthWeekday', month: 2, weekday: 1, nth: 3, name: "Presidents' Day" },
        { type: 'lastWeekday', month: 5, weekday: 1, name: 'Memorial Day' },
        { type: 'fixed', month: 6, day: 19, name: 'Juneteenth' },
        { type: 'fixed', month: 7, day: 4, name: 'Independence Day' },
        { type: 'nthWeekday', month: 9, weekday: 1, nth: 1, name: 'Labor Day' },
        { type: 'nthWeekday', month: 10, weekday: 1, nth: 2, name: 'Columbus Day' },
        { type: 'fixed', month: 11, day: 11, name: 'Veterans Day' },
        { type: 'nthWeekday', month: 11, weekday: 4, nth: 4, name: 'Thanksgiving' },
        { type: 'fixed', month: 12, day: 25, name: 'Christmas Day' },
    ]},
];

// Returns [{ date: 'YYYY-MM-DD', name }] for a country code and year.
const getHolidays = (countryCode, year) => {
    const country = HOLIDAY_COUNTRIES.filter((c) => c.code === countryCode)[0];
    if (typeof country === 'undefined') return [];
    const seen = {};
    const result = [];
    country.rules.forEach((rule) => {
        const d = resolveRule(rule, year);
        if (d === null) return;
        const iso = toISO(d);
        if (seen[iso]) return;
        seen[iso] = true;
        result.push({ date: iso, name: rule.name });
    });
    result.sort((a, b) => (a.date < b.date ? -1 : 1));
    return result;
};

const countryName = (countryCode) => {
    const country = HOLIDAY_COUNTRIES.filter((c) => c.code === countryCode)[0];
    return typeof country === 'undefined' ? countryCode : country.name;
};

// Match saved non-working exception dates back to the country calendars they came
// from. An organisation can hold several calendars, so every complete country/year
// calendar found in the saved dates is returned.
const matchSavedHolidayCalendars = (isoDates) => {
    const nonWorking = {};
    (isoDates || []).forEach((date) => {
        if (date) nonWorking[date] = true;
    });
    const dates = Object.keys(nonWorking);
    if (!dates.length) return [];

    const years = [];
    dates.forEach((date) => {
        const year = parseInt(date.slice(0, 4), 10);
        if (!isNaN(year) && years.indexOf(year) === -1) years.push(year);
    });
    years.sort((a, b) => a - b);

    const matches = [];
    years.forEach((year) => {
        HOLIDAY_COUNTRIES.forEach((country) => {
            const holidays = getHolidays(country.code, year);
            if (!holidays.length) return;
            let matched = 0;
            holidays.forEach((holiday) => {
                if (nonWorking[holiday.date]) matched++;
            });
            // Applied calendars save their full generated date set. Requiring an
            // exact match prevents one calendar being inferred from shared dates
            // in another calendar (for example Malaysia and Singapore).
            if (matched !== holidays.length) return;
            matches.push({ country: country.code, name: country.name, year, matched });
        });
    });
    return matches;
};

// Kept for callers that only need a single best guess.
const matchSavedHolidayCalendar = (isoDates) => {
    const matches = matchSavedHolidayCalendars(isoDates);
    if (!matches.length) return null;
    return matches.slice().sort((a, b) => (b.matched - a.matched) || (b.year - a.year))[0];
};

const getHolidayName = (isoDate, countryCodes) => {
    if (!isoDate) return "";
    const year = parseInt(isoDate.slice(0, 4), 10);
    if (isNaN(year)) return "";
    const lookup = (code) => {
        const holidays = getHolidays(code, year);
        for (let i = 0; i < holidays.length; i++) {
            if (holidays[i].date === isoDate) return holidays[i].name;
        }
        return "";
    };
    let preferred = [];
    if (Array.isArray(countryCodes)) {
        preferred = countryCodes;
    } else if (countryCodes) {
        preferred = [countryCodes];
    }
    for (let i = 0; i < preferred.length; i++) {
        const name = lookup(preferred[i]);
        if (name) return name;
    }
    return "";
};

export {
    HOLIDAY_COUNTRIES,
    countryName,
    getHolidays,
    getHolidayName,
    matchSavedHolidayCalendar,
    matchSavedHolidayCalendars
};
